<template>
  <div class="d-flex flex-column min-vh-100">
    <!-- Header -->
    <header class="pre-schedule-header py-3 border-bottom bg-white">
      <div class="container">
        <div class="d-flex justify-content-center align-items-center">
          <a href="https://ubbimed.com.br" class="text-decoration-none">
            <img :src="require('@/assets/images/logo_ubbi.png')" height="50" alt="Ubbimed" class="logo-light-mode">
          </a>
        </div>
      </div>
    </header>

    <!-- Content -->
    <section class="section-two bg-light flex-grow-1 py-4">
      <div class="container">
      <div v-if="state === 'loading'" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="mt-3">Carregando formulário...</p>
      </div>

      <div v-else-if="state === 'error'" class="text-center">
        <div class="card mt-4" style="max-width: 600px; margin: 0 auto;">
          <div class="card-body text-center p-5">
            <div class="mb-4">
              <div class="error-icon-wrapper mx-auto mb-3">
                <i class="bi bi-exclamation-triangle-fill error-icon"></i>
              </div>
              <h4 class="fw-bold text-dark mb-3">Erro ao carregar formulário</h4>
              <p class="text-muted mb-4">{{ errorMessage }}</p>
            </div>
            <div class="d-grid gap-2">
              <button class="btn btn-primary btn-lg" @click="$router.push('/schedules')">
                <i class="bi bi-arrow-left me-2"></i>
                Voltar para Agendamentos
              </button>
              <button class="btn btn-outline-secondary" @click="loadForm()">
                <i class="bi bi-arrow-clockwise me-2"></i>
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="state === 'success'" class="text-center">
        <div class="card mt-4" style="max-width: 600px; margin: 0 auto;">
          <div class="card-body text-center p-5">
            <div class="mb-4">
              <div class="success-icon-wrapper mx-auto mb-3">
                <i class="bi bi-check-circle-fill success-icon"></i>
              </div>
              <h4 class="fw-bold text-dark mb-3">Formulário enviado com sucesso!</h4>
              <p class="text-muted mb-4">Obrigado por responder o formulário de pré-agendamento.</p>
            </div>
            <div class="d-grid">
              <button class="btn btn-primary btn-lg" @click="$router.push('/schedules')">
                <i class="bi bi-arrow-left me-2"></i>
                Voltar para Agendamentos
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="state === 'loaded' && form">
        <h3 class="text-center mb-3">{{ form.form_name }}</h3>
        <p v-if="form.form_description" class="text-center mb-4">{{ form.form_description }}</p>

        <div class="card mt-4">
          <div class="card-body">
            <form @submit.prevent="submitForm">
              <div v-for="(question, index) in sortedQuestions" :key="question.id_pre_schedule_question" class="mb-4">
                <div class="form-group">
                  <label class="form-label fw-bold">
                    {{ question.question_text }}
                    <span v-if="question.required" class="text-danger">*</span>
                  </label>

                  <!-- Text Input -->
                  <input
                    v-if="question.question_type === 'text'"
                    type="text"
                    class="form-control"
                    v-model="answers[question.id_pre_schedule_question]"
                    :required="question.required"
                    :placeholder="question.required ? 'Campo obrigatório' : 'Digite sua resposta'"
                  />

                  <!-- Radio Buttons -->
                  <div v-else-if="question.question_type === 'radio'">
                    <div v-for="(option, optIndex) in question.options" :key="optIndex" class="form-check mb-2">
                      <input
                        class="form-check-input"
                        type="radio"
                        :name="'question_' + question.id_pre_schedule_question"
                        :id="'option_' + question.id_pre_schedule_question + '_' + optIndex"
                        :value="option.value || option.label"
                        v-model="answers[question.id_pre_schedule_question]"
                        :required="question.required"
                      />
                      <label class="form-check-label" :for="'option_' + question.id_pre_schedule_question + '_' + optIndex">
                        {{ option.label || option.value }}
                      </label>
                    </div>
                  </div>

                  <!-- Checkboxes -->
                  <div v-else-if="question.question_type === 'checkbox'">
                    <div v-for="(option, optIndex) in question.options" :key="optIndex" class="form-check mb-2">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'option_' + question.id_pre_schedule_question + '_' + optIndex"
                        :value="option.value || option.label"
                        v-model="answers[question.id_pre_schedule_question]"
                        :required="question.required && (!answers[question.id_pre_schedule_question] || answers[question.id_pre_schedule_question].length === 0)"
                      />
                      <label class="form-check-label" :for="'option_' + question.id_pre_schedule_question + '_' + optIndex">
                        {{ option.label || option.value }}
                      </label>
                    </div>
                  </div>

                  <!-- File Upload -->
                  <div v-else-if="question.question_type === 'file'">
                    <input
                      type="file"
                      class="form-control"
                      :id="'file_' + question.id_pre_schedule_question"
                      @change="handleFileChange($event, question.id_pre_schedule_question)"
                      :required="question.required"
                      accept="image/*,application/pdf,.doc,.docx"
                    />
                    <div v-if="files[question.id_pre_schedule_question]" class="mt-2">
                      <small class="text-muted">Arquivo selecionado: {{ files[question.id_pre_schedule_question].name }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-grid gap-2 mt-4">
                <button type="submit" class="btn btn-primary btn-lg" :disabled="submitting">
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ submitting ? 'Enviando...' : 'Enviar Formulário' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="pre-schedule-footer py-3 border-top bg-white mt-auto">
      <div class="container">
        <div class="text-center">
          <p class="mb-0 text-muted small">
            © {{ new Date().getFullYear() }} Ubbimed. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";

export default {
  name: "PreScheduleForm",
  data() {
    return {
      state: 'loading', // loading, loaded, error, success
      errorMessage: '',
      token: null,
      formId: null,
      scheduleId: null,
      form: null,
      answers: {},
      files: {},
      submitting: false,
    }
  },
  computed: {
    sortedQuestions() {
      if (!this.form || !this.form.questions) {
        return [];
      }
      return [...this.form.questions].sort((a, b) => a.question_order - b.question_order);
    }
  },
  created() {
    this.decodeToken();
    if (this.token && this.formId && this.scheduleId) {
      this.authUsingToken();
      this.loadForm();
    } else {
      this.state = 'error';
      this.errorMessage = 'Token inválido ou parâmetros ausentes.';
    }
  },
  methods: {
    decodeToken() {
      try {
        const tokenBase64 = this.$route.params.token;
        const decoded = atob(tokenBase64);
        const data = JSON.parse(decoded);
        
        if (!data.token || !data.id_pre_schedule_form || !data.id_sched) {
          throw new Error('Token não contém todas as informações necessárias');
        }

        this.token = data.token;
        this.formId = data.id_pre_schedule_form;
        this.scheduleId = data.id_sched;
      } catch (e) {
        console.error('Erro ao decodificar token:', e);
        this.state = 'error';
        this.errorMessage = 'Token inválido. Por favor, verifique o link recebido.';
      }
    },
    authUsingToken() {
      return this.$store.dispatch('auth/loginUsingToken', this.token)
        .catch((e) => {
          console.error('Erro ao autenticar:', e);
          this.state = 'error';
          this.errorMessage = 'Erro ao autenticar. Por favor, tente novamente.';
        });
    },
    loadForm() {
      http.get(`/pre-schedule-answers/${this.$route.params.token}`)
        .then((response) => {
          this.form = response.data;
          this.initializeAnswers();
          this.state = 'loaded';
        })
        .catch((e) => {
          console.error('Erro ao carregar formulário:', e);
          if (e.response?.status === 409) {
            this.state = 'success';
            this.errorMessage = e.response?.data?.message || 'Este formulário já foi respondido.';
          } else {
            this.state = 'error';
            this.errorMessage = e.response?.data?.message || 'Erro ao carregar formulário. Por favor, tente novamente.';
          }
        });
    },
    initializeAnswers() {
      if (!this.form || !this.form.questions) {
        return;
      }

      this.form.questions.forEach(question => {
        if (question.question_type === 'checkbox') {
          this.answers[question.id_pre_schedule_question] = [];
        } else {
          this.answers[question.id_pre_schedule_question] = null;
        }
      });
    },
    handleFileChange(event, questionId) {
      const file = event.target.files[0];
      this.files[questionId] = file || null;
      this.answers[questionId] = file ? file.name : null;
    },
    async submitForm() {
      for (const question of this.sortedQuestions) {
        if (question.required) {
          const answer = question.question_type === 'file'
            ? this.files[question.id_pre_schedule_question]
            : this.answers[question.id_pre_schedule_question];
          if (!answer || (Array.isArray(answer) && answer.length === 0)) {
            Alert.error(`Por favor, responda a pergunta: ${question.question_text}`);
            return;
          }
        }
      }

      this.submitting = true;

      try {
        const formData = new FormData();

        for (const question of this.sortedQuestions) {
          const questionId = question.id_pre_schedule_question;
          if (question.question_type === 'file') {
            if (this.files[questionId]) {
              formData.append(`files[${questionId}]`, this.files[questionId]);
            }
            continue;
          }

          const answer = this.answers[questionId];
          if (Array.isArray(answer)) {
            answer.forEach((value) => formData.append(`answers[${questionId}][]`, value));
          } else if (answer != null && answer !== '') {
            formData.append(`answers[${questionId}]`, answer);
          }
        }

        await http.post(`/pre-schedule-answers/${this.$route.params.token}`, formData);

        this.state = 'success';
      } catch (e) {
        console.error('Erro ao enviar formulário:', e);
        Alert.error(e.response?.data?.message || 'Erro ao enviar formulário. Por favor, tente novamente.');
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style scoped>
.pre-schedule-header {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.pre-schedule-footer {
  margin-top: auto;
}

.card {
  max-width: 600px;
  margin: 0 auto;
}

.form-label {
  margin-bottom: 0.5rem;
  font-size: 1rem;
}

.form-control {
  border-radius: 0.5rem;
}

.form-check-input {
  margin-top: 0.25rem;
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  font-size: 1.1rem;
}

.error-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon {
  font-size: 3rem;
  color: #dc2626;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon {
  font-size: 3rem;
  color: #059669;
}

@media (max-width: 576px) {
  .card {
    margin: 0 1rem;
  }

  .form-label {
    font-size: 0.95rem;
  }

  .error-icon-wrapper,
  .success-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .error-icon,
  .success-icon {
    font-size: 2.5rem;
  }
}
</style>
